import React, { useEffect, useReducer } from 'react';
import Metrics from '@spotahome/soyuz-metrics';
import { Modal, useModalHandle } from '@spotahome/ui-library';
import '@spotahome/ui-library/lib/components/Button.css';
import '@spotahome/ui-library/lib/components/Modal.css';
import '@spotahome/ui-library/lib/components/CloseButton.css';

import { useMatcherContext } from '@common/context/MatcherContext';

import Onboarding from './Onboarding';
import Final from './Final';
import Swiper from './Swiper/Swiper';
import MatcherFilters from './MatcherFilters';
import NoMatches from './NoMatches';

import './MatcherModal.scss';

const ONBOARDING = 'onboarding';
const SWIPER = 'swiper';
const FINAL = 'final';
const FILTERS = 'filters';
const NO_MATCHES = 'noMatches';
// possible actions
const NEXT = 'next';
const PREVIOUS = 'previous';
const GO_TO_FILTERS = 'goToFilters';
const GO_TO_NO_MATCHES = 'goToNoMatches';

const PAGES = {
  0: ONBOARDING,
  1: FILTERS,
  2: SWIPER,
  3: FINAL,
  4: NO_MATCHES
};

const findIndexWithPageName = pageName =>
  Object.values(PAGES).indexOf(pageName);

const getInitialState = initialPage => ({
  currentPageIndex: findIndexWithPageName(initialPage)
});

const reducer = (state, action) => {
  switch (action) {
    case GO_TO_NO_MATCHES:
      return { currentPageIndex: findIndexWithPageName(NO_MATCHES) };
    case GO_TO_FILTERS:
      return { currentPageIndex: findIndexWithPageName(FILTERS) };
    case NEXT:
      return { currentPageIndex: state.currentPageIndex + 1 };
    case PREVIOUS:
      return { currentPageIndex: state.currentPageIndex - 1 };
    default:
      console.log('unknown action type:', action);
      return state;
  }
};

// TODO: consume homecards from MatcherContext directly
const MatcherModal = () => {
  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

  useEffect(() => {
    handleModalOpen();
    Metrics.ga.sendEvent('Search', 'matcher-modal', 'open');
  }, [handleModalOpen]);

  const {
    skipOnboarding,
    homecards,
    isEnd,
    resetFilters,
    hideMatcher
  } = useMatcherContext();
  const initialStep = skipOnboarding ? FILTERS : ONBOARDING;
  const [state, dispatch] = useReducer(reducer, getInitialState(initialStep));

  const onClose = () => {
    hideMatcher();
    handleModalClose();
    resetFilters();
  };

  useEffect(() => {
    if (isEnd) {
      dispatch(GO_TO_NO_MATCHES);
      resetFilters();
    }
  }, [isEnd, resetFilters]);

  const onNext = () => dispatch(NEXT);
  const onPrevious = () => dispatch(PREVIOUS);
  const goToFilters = () => dispatch(GO_TO_FILTERS);

  // eslint-disable-next-line react/prop-types
  const renderModalContent = ({ currentPageIndex }) => {
    switch (PAGES[currentPageIndex]) {
      case NO_MATCHES:
        return <NoMatches goToFilters={goToFilters} />;
      case FINAL:
        return <Final />;
      case SWIPER:
        return (
          <Swiper
            goToFilters={goToFilters}
            homecards={homecards}
            close={onClose}
            next={onNext}
          />
        );
      case FILTERS:
        return <MatcherFilters next={onNext} close={onClose} />;
      case ONBOARDING:
      default:
        return (
          <Onboarding close={onClose} next={onNext} previous={onPrevious} />
        );
    }
  };

  return (
    <Modal className={'matcher-modal'} size="fullscreen" isOpen={isModalOpen}>
      {renderModalContent(state)}
    </Modal>
  );
};

export default MatcherModal;
